import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { DataNode } from "antd/lib/tree";
import { documentsTableStore } from "./DocumentsTableStore";
import { stringSort } from "utils/defaultSort";

export interface ExtendedDataNode extends DataNode, Pick<API_TreeElement, "directFiles" | "external"> {
  id?: string | number;
  hasChildren?: boolean;
  children?: ExtendedDataNode[];
  linkId?: number;
}

function sortSubfolders(subfolders: API_TreeElement[]): API_TreeElement[] {
  if (!subfolders) {
    return subfolders;
  }
  return [...subfolders].sort((a, b) => stringSort(a, b, "name"));
}

// used to convert response object into object consumable to Tree component
export function treeElementToDataNode(item: API_TreeElement): ExtendedDataNode {
  if (item.linkId) {
    documentsTableStore.setLinkIdByFolderId(item.id, item.linkId);
  }
  return {
    id: item.id,
    key: item.id,
    hasChildren: !item.children,
    directFiles: item.directFiles,
    external: item.external,
    linkId: item.linkId,
    children: sortSubfolders(item.subfolders)?.map(treeElementToDataNode),
    title: item.name,
    // disabled: item.unselectable
  };
}

// used for extending props of DataNode objects with class that would show that folder does not have files
function treeElementToDataNodeWithProps(item: API_TreeElement): ExtendedDataNode {
  if (item.linkId) {
    documentsTableStore.setLinkIdByFolderId(item.id, item.linkId);
  }
  if (item.directFiles > 0) {
    return treeElementToDataNode(item);
  } else {
    return {
      id: item.id,
      key: item.id,
      hasChildren: !item.children,
      directFiles: item.directFiles,
      external: item.external,
      linkId: item.linkId,
      children: sortSubfolders(item.subfolders)?.map(treeElementToDataNodeWithProps),
      title: item.name,
      className: "empty-folder",
      // disabled: item.unselectable
    };
  }
}

// Used in Documents module to show folders structure available for all project members.
export class FoldersStore extends CoreStore {
  data: ExtendedDataNode = { key: null };
  expandedKeysMap: Map<number, number> = new Map();

  constructor() {
    super();

    makeObservable(this, {
      data: observable.shallow,
      expandedKeysMap: observable,
      clear: action.bound,
      setExpandedKey: action.bound,
      fetchFolder: action.bound,
      rootFolderId: computed,
      expandedKeys: computed,
      treeData: computed,
      fetch: action.bound,
    });
  }

  url(id?: number) {
    if (id) {
      return API_URL.FOLDERS.getSubfolders(projectsDataStore.projectId, id);
    }
    return API_URL.FOLDERS.getTreeRoot(projectsDataStore.projectId);
  }

  clear() {
    this.data = { key: null };
    this.expandedKeysMap = new Map();
  }

  setExpandedKey(key: number, expand: boolean) {
    if (expand) {
      this.expandedKeysMap.set(key, key);
    } else {
      this.expandedKeysMap.delete(key);
    }
  }

  @handleStoreState()
  async fetch(id?: number) {
    try {
      // If folder id is provided - fetch path to current folder
      let url: string;
      const linkId = documentsTableStore.getLinkIdByFolderId(id);
      if (linkId) {
        if (id) {
          url = API_URL.FOLDERS.getFullPathForExternalLink(projectsDataStore.projectId, linkId);
          url += "?folder=" + id;
        } else {
          // there's no "rootdirs" for linked folder - using endpoint for regular folders
          url = this.url();
        }
      } else {
        url = id ? API_URL.FOLDERS.getFullPath(projectsDataStore.projectId, id) : this.url();
      }
      const response: API_TreeElement = await API.getData(url);
      runInAction(() => {
        this.data = treeElementToDataNodeWithProps(response);
        // DEV-6173 Remove className for root folder to not use grey color on root directory even if no directFiles
        this.data.className = "";
        this.setExpandedKey(response.id, true);
        // if (id !== this.rootFolderId) {
        //   this.findInTree(this.data.subfolders, id);
        // }
      });
    } catch (error) {
      throw error;
    }
  }

  async reFetch() {
    this.fetch(documentsTableStore.currentFolderId);
  }

  /**
   * Finds seeking folder, requests it's subfolder and injects it's content into
   */
  async fetchFolder(folderId: number, forceUpdate = false) {
    try {
      const copy = { ...this.data };
      // FIXME: This is a bottle neck of performance. Try to improve finding of proper node.
      let foundItem = this.findInTree(copy.children, folderId);
      if (!foundItem) {
        foundItem = copy;
      }
      const linkId = documentsTableStore.getLinkIdByFolderId(folderId);

      if (forceUpdate || (!foundItem.hasChildren && !foundItem.children)) {
        let response: API_TreeElement;
        if (linkId || foundItem?.linkId) {
          // const realFolderId = `${folderId}`.replace("L", "");
          const params = { folder: folderId };
          response = await API.getData(
            API_URL.FOLDERS.getSubdirsForExternalLink(projectsDataStore.projectId, linkId),
            params
          );
        } else {
          response = await API.getData(this.url(folderId));
        }
        runInAction(() => {
          foundItem.children = sortSubfolders(response.subfolders)?.map(treeElementToDataNodeWithProps) || [];
          foundItem.isLeaf = !response.children;
          this.data = copy;
        });
      }
    } catch (error) {
      throw error;
    }
  }

  get rootFolderId() {
    return this.data.id;
  }

  get expandedKeys() {
    return Array.from(this.expandedKeysMap.keys());
  }

  get treeData() {
    return [this.data];
  }

  // Traverse through tree nodes to return reference of node by id
  private findInTree = (array: ExtendedDataNode[], id: number) => {
    if (array && array.length) {
      let item: ExtendedDataNode;
      for (let i = 0; i < array.length; i++) {
        if (array[i].id === id) {
          item = array[i];
          break;
        } else if (array[i].children) {
          // in order to expand every folder on the way to seeked folder
          // this.setExpandedKey(array[i].id, true);
          const found = this.findInTree(array[i].children, id);
          if (found) {
            item = found;
          }
        }
      }
      return item;
    } else {
      return { key: null };
    }
  };
}

export const foldersStore = new FoldersStore();
